/**
 * Petstyle24 – Warenkorb-Adapter (Build-Plan v2 §4.4, Phase C).
 *
 * Einzige Stelle im Theme, die den Shopify-Warenkorb verändert und Warenkorb-HTML ersetzt.
 * Keine zweite Cart-Engine: Mengen, Preise, Rabatte und Summen rendert Shopify
 * (Cart AJAX API + Section Rendering API). Im Browser wird nichts berechnet; einzig die
 * Mengenzahl im Eingabefeld wird bis zur Antwort von Shopify vorläufig angezeigt.
 *
 * window.Petstyle24.cart
 *   add(variantId, quantity = 1, { trigger, open = true }) → Promise<{ ok, error? }>
 *     Go-live-Runde (Wunsch 3, D-055 Nr. 7): variantId auch als Liste [{ id, quantity }, …] – mehrere Artikel in einer
 *     Anfrage (Cart API items[]); Bundle-Block sections/product-bundle.liquid
 *   change(lineOrKey, quantity)   → Promise<{ ok, error? }>  (Line-Item-Key oder Position ab 1)
 *   applyDiscount(code)           → Promise<{ ok, applicable, error? }>
 *   open(trigger) · close() · refresh()
 * Events: ps:cart:updated { source } · ps:cart:error { source, message }
 *
 * Cart-Vertrag: #PsCartDrawer, #PsCartDrawer-Form, #PsCartDrawer-Items, #PsCartDrawer-Checkout,
 * #PsCartCount. Sections: ps-cart-drawer, ps-cart-count (+ main-cart auf /cart).
 * Jede Ansicht trägt data-ps-cart-view="<section-id>"; nur deren Inhalt wird ersetzt.
 *
 * Sicherheit (05b §19, 05c §18): Ersetzt wird nur HTML aus der Section Rendering API des
 * eigenen Shops (gleiche Herkunft, DOMParser, keine Skriptausführung). Meldungen werden
 * per textContent gesetzt. Keine Preise, Rabatte oder Bestände werden im Client festgelegt.
 *
 * Progressive Enhancement: Ohne JavaScript funktionieren /cart/add, /cart (updates[],
 * checkout), url_to_remove und /cart/update nativ.
 */
(() => {
  'use strict';

  const PS = window.Petstyle24;
  if (!PS || PS.cart) return;

  const QTY_MIN = 1;
  const QTY_MAX = 10;
  const COUNT_SECTION = 'ps-cart-count';
  const DEBOUNCE_MS = 350;
  const routes = PS.config.routes || {};
  const strings = PS.config.strings || {};

  const text = (key, values) => {
    let value = strings[key] || strings.errorGeneric || '';
    Object.entries(values || {}).forEach(([name, replacement]) => {
      value = value.split(`{${name}}`).join(String(replacement));
    });
    return value;
  };

  const drawer = () => document.getElementById('PsCartDrawer');
  const views = () => Array.from(document.querySelectorAll('[data-ps-cart-view]'));

  const sectionIds = () => {
    const ids = new Set([COUNT_SECTION]);
    views().forEach((view) => {
      if (view.dataset.psCartView) ids.add(view.dataset.psCartView);
    });
    return Array.from(ids);
  };

  const clampQuantity = (value, min = 0) => {
    const number = Math.round(Number(value));
    if (!Number.isFinite(number)) return min;
    return Math.min(QTY_MAX, Math.max(min, number));
  };

  /* ---------- Warteschlange: Anfragen nacheinander, keine überholenden Antworten ---------- */
  let queue = Promise.resolve();
  const enqueue = (task) => {
    const run = queue.then(task, task);
    queue = run.catch(() => undefined);
    return run;
  };

  /* ---------- Netzwerk ---------- */
  const jsonEndpoint = (route) => (typeof route === 'string' && route ? PS.sameOriginUrl(`${route}.js`) : null);

  const postJSON = async (route, body) => {
    const url = jsonEndpoint(route);
    if (!url) return { ok: false, status: 0, data: null };
    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      let data = null;
      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }
      return { ok: response.ok, status: response.status, data };
    } catch (error) {
      return { ok: false, status: 0, data: null };
    }
  };

  const getCart = async () => {
    const url = jsonEndpoint(routes.cart);
    if (!url) throw new Error('Route fehlt');
    const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  };

  const withSections = (body) => ({
    ...body,
    sections: sectionIds().join(','),
    sections_url: window.location.pathname,
  });

  // Kundentexte von Shopify (z. B. Bestandsgrenze) übernehmen, sonst allgemeiner Hinweis.
  // Nie technische Details anzeigen.
  const errorMessage = (data) => {
    const description = data && typeof data.description === 'string' ? data.description.trim() : '';
    if (description && description.length <= 240 && !/[<>]/.test(description)) return description;
    return text('errorGeneric');
  };

  /* ---------- Darstellung ---------- */
  const setBusy = (busy) => {
    views().forEach((view) => view.setAttribute('aria-busy', busy ? 'true' : 'false'));
  };

  const focusKeyOf = (element) => {
    const holder = element && element.closest ? element.closest('[data-ps-focus-key]') : null;
    return holder ? holder.dataset.psFocusKey : null;
  };

  const findByKey = (view, key) => {
    if (!key) return null;
    return Array.from(view.querySelectorAll('[data-ps-focus-key]')).find((node) => node.dataset.psFocusKey === key) || null;
  };

  const restoreFocus = (view, key) => {
    let target = findByKey(view, key);
    if (!target && key) {
      // Position entfernt oder Element fehlt: Mengenfeld derselben Position, sonst Überschrift
      const lineKey = key.slice(key.indexOf(':') + 1);
      target = findByKey(view, `qty:${lineKey}`);
    }
    if (!target) target = view.querySelector('[data-ps-cart-focus-fallback]');
    if (target) target.focus({ preventScroll: true });
  };

  const syncDiscountButtons = (root) => {
    root.querySelectorAll('[data-ps-cart-discount-input]').forEach((input) => {
      const button = root.querySelector(`[data-ps-cart-discount-apply][form="${CSS.escape(input.getAttribute('form') || '')}"]`);
      if (button) button.disabled = input.value.trim() === '';
    });
  };

  // DOMParser parst ohne Scripting: <noscript>-Inhalte würden zu echten Elementen (z. B. ein
  // zweiter Submit-Button im Formular). Beim ersten Rendern mit JavaScript sind sie inert.
  // Geleert entsteht nach AJAX dieselbe Elementstruktur wie beim ersten Rendern (M1.1).
  const neutralizeNoscript = (root) => {
    root.querySelectorAll('noscript').forEach((node) => node.replaceChildren());
  };

  const replaceView = (view, html) => {
    const viewId = view.dataset.psCartView;
    const fresh = PS.parseHTML(html).querySelector(`[data-ps-cart-view="${CSS.escape(viewId)}"]`);
    if (!fresh) return;
    neutralizeNoscript(fresh);

    const active = document.activeElement;
    const hadFocus = Boolean(active && active !== document.body && view.contains(active));
    const focusKey = hadFocus ? focusKeyOf(active) : null;
    const list = view.querySelector('[data-ps-cart-items]');
    const listScroll = list ? list.scrollTop : 0;
    const discount = view.querySelector('[data-ps-cart-discount]');
    const discountOpen = Boolean(discount && discount.open);

    view.replaceChildren(...Array.from(fresh.childNodes, (node) => document.importNode(node, true)));

    const newList = view.querySelector('[data-ps-cart-items]');
    if (newList) newList.scrollTop = listScroll;
    const newDiscount = view.querySelector('[data-ps-cart-discount]');
    if (newDiscount && discountOpen) newDiscount.open = true;
    syncDiscountButtons(view);
    if (hadFocus) restoreFocus(view, focusKey);
  };

  const replaceCount = (html) => {
    const fresh = PS.parseHTML(html).getElementById('PsCartCount');
    const current = document.getElementById('PsCartCount');
    if (fresh && current) current.replaceWith(document.importNode(fresh, true));
  };

  const render = (sections) => {
    if (!sections || typeof sections !== 'object') return false;
    if (typeof sections[COUNT_SECTION] === 'string') replaceCount(sections[COUNT_SECTION]);
    views().forEach((view) => {
      const html = sections[view.dataset.psCartView];
      if (typeof html === 'string') replaceView(view, html);
    });
    return true;
  };

  const refreshNow = async () => {
    try {
      render(await PS.fetchSections(window.location.pathname, sectionIds()));
      return true;
    } catch (error) {
      return false;
    }
  };

  const refresh = () => enqueue(refreshNow);

  /* ---------- Drawer ---------- */
  const open = (trigger) => {
    const dialog = drawer();
    if (!dialog) return false;
    return PS.dialog.open(dialog, trigger || document.activeElement);
  };

  const close = () => {
    const dialog = drawer();
    if (dialog) PS.dialog.close(dialog);
  };

  /* ---------- Operationen ---------- */
  const succeed = (source, data, message) => {
    render(data && data.sections);
    PS.emit('cart:updated', { source });
    if (message) PS.announce(message);
    return { ok: true };
  };

  const fail = (source, message) => {
    PS.emit('cart:error', { source, message });
    PS.announce(message);
    return { ok: false, error: message };
  };

  const add = (variantId, quantity = 1, options = {}) =>
    enqueue(async () => {
      // Wunsch 3 (D-055 Nr. 7): eine Variante oder eine Liste [{ id, quantity }] – jeder Eintrag geprüft, eine Anfrage
      const entries = Array.isArray(variantId) ? variantId : [{ id: variantId, quantity }];
      const items = entries.map((entry) => ({ id: Number(entry && entry.id), quantity: clampQuantity(entry && entry.quantity, QTY_MIN) }));
      if (!items.length || items.some((item) => !Number.isInteger(item.id) || item.id <= 0)) return fail('add', text('errorGeneric'));

      setBusy(true);
      const result = await postJSON(routes.cartAdd, withSections({ items }));
      setBusy(false);

      if (!result.ok) {
        // Runde W3 (D-065 Nr. 5): Bei zu großer Menge legt Shopify den verfügbaren Rest trotzdem in den Warenkorb und
        // meldet das als Fehler – Zähler und Ansichten wie bei change() nachladen; der Drawer öffnet nur nach Erfolg
        await refreshNow();
        return fail('add', errorMessage(result.data));
      }
      const outcome = succeed('add', result.data, text('cartAdded'));
      if (options.open !== false) open(options.trigger);
      return outcome;
    });

  const lineReference = (lineOrKey) => {
    if (typeof lineOrKey === 'string' && lineOrKey.includes(':')) return { id: lineOrKey };
    return { line: Number(lineOrKey) };
  };

  const change = (lineOrKey, quantity) =>
    enqueue(async () => {
      const qty = clampQuantity(quantity, 0);

      setBusy(true);
      const result = await postJSON(routes.cartChange, withSections({ ...lineReference(lineOrKey), quantity: qty }));
      setBusy(false);

      if (!result.ok) {
        await refreshNow();
        return fail('change', errorMessage(result.data));
      }
      return succeed('change', result.data, text(qty === 0 ? 'cartRemoved' : 'cartUpdated'));
    });

  const appliedCodes = (cart) => {
    const codes = new Set();
    const collect = (application) => {
      if (application && application.type === 'discount_code' && application.title) codes.add(application.title);
    };
    (cart.cart_level_discount_applications || []).forEach(collect);
    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => collect(allocation && allocation.discount_application));
    });
    return Array.from(codes);
  };

  const sameCode = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();

  const renderedCode = (code) =>
    views().some((view) => Array.from(view.querySelectorAll('[data-ps-cart-code]')).some((node) => sameCode(node.dataset.psCartCode, code)));

  const showDiscountError = (message, code) => {
    views().forEach((view) => {
      const details = view.querySelector('[data-ps-cart-discount]');
      const error = view.querySelector('[data-ps-cart-discount-error]');
      const field = view.querySelector('[data-ps-cart-discount-field]');
      const input = view.querySelector('[data-ps-cart-discount-input]');
      if (details) details.open = true;
      if (error) error.textContent = message;
      if (field) field.classList.add('ps-field--error');
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        if (typeof code === 'string') input.value = code;
      }
      syncDiscountButtons(view);
    });
  };

  const applyDiscount = (code) =>
    enqueue(async () => {
      const value = String(code || '').trim();
      if (!value) return { ok: false, applicable: false };

      setBusy(true);
      let existing = [];
      try {
        existing = appliedCodes(await getCart());
      } catch (error) {
        existing = [];
      }
      const codes = existing.some((entry) => sameCode(entry, value)) ? existing : [...existing, value];
      const result = await postJSON(routes.cartUpdate, withSections({ discount: codes.join(',') }));

      if (!result.ok) {
        setBusy(false);
        await refreshNow();
        const message = errorMessage(result.data);
        showDiscountError(message, value);
        PS.emit('cart:error', { source: 'discount', message });
        return { ok: false, applicable: false, error: message };
      }

      render(result.data && result.data.sections);
      const reported = result.data && Array.isArray(result.data.discount_codes)
        ? result.data.discount_codes.find((entry) => entry && sameCode(entry.code, value))
        : null;
      const applicable = reported ? Boolean(reported.applicable) : renderedCode(value);

      if (!applicable) {
        // Nicht anwendbaren Code nicht im Warenkorb stehen lassen
        const reverted = await postJSON(routes.cartUpdate, withSections({ discount: existing.join(',') }));
        if (reverted.ok) render(reverted.data && reverted.data.sections);
        setBusy(false);
        const message = text('discountInvalid');
        showDiscountError(message, value);
        PS.emit('cart:error', { source: 'discount', message });
        return { ok: false, applicable: false, error: message };
      }

      setBusy(false);
      PS.emit('cart:updated', { source: 'discount' });
      PS.announce(text('discountApplied', { code: value }));
      return { ok: true, applicable: true };
    });

  /* ---------- Mengen: Zahl sofort zeigen, Shopify nach kurzer Pause fragen ---------- */
  const pending = new Map();

  const syncStepButtons = (input) => {
    const group = input.closest('[data-ps-qty]');
    if (!group) return;
    const value = Number(input.value);
    group.querySelectorAll('[data-ps-qty-step]').forEach((button) => {
      const step = Number(button.dataset.psQtyStep);
      const blocked = step < 0 ? value <= QTY_MIN : value >= QTY_MAX;
      if (blocked) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    });
  };

  const scheduleChange = (input) => {
    const key = input.dataset.psLine;
    if (!key) return;
    const qty = clampQuantity(input.value, QTY_MIN);
    input.value = String(qty);
    syncStepButtons(input);
    window.clearTimeout(pending.get(key));
    pending.set(
      key,
      window.setTimeout(() => {
        pending.delete(key);
        change(key, qty);
      }, DEBOUNCE_MS)
    );
  };

  const stepQuantity = (button) => {
    if (button.getAttribute('aria-disabled') === 'true') return;
    const group = button.closest('[data-ps-qty]');
    const input = group ? group.querySelector('[data-ps-qty-input]') : null;
    if (!input) return;
    input.value = String(clampQuantity(Number(input.value) + Number(button.dataset.psQtyStep), QTY_MIN));
    scheduleChange(input);
  };

  /* ---------- Ereignisse (Delegation) ---------- */
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-ps-cart-open]');
    if (opener) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!drawer()) return;
      event.preventDefault();
      open(opener);
      return;
    }

    if (target.closest('[data-ps-cart-close]')) {
      event.preventDefault();
      close();
      return;
    }

    const step = target.closest('[data-ps-qty-step]');
    if (step) {
      event.preventDefault();
      stepQuantity(step);
      return;
    }

    const remove = target.closest('[data-ps-cart-remove]');
    if (remove && remove.dataset.psLine) {
      event.preventDefault();
      window.clearTimeout(pending.get(remove.dataset.psLine));
      pending.delete(remove.dataset.psLine);
      change(remove.dataset.psLine, 0);
      return;
    }

    // Link im Drawer (z. B. leerer Warenkorb → Beratung): Drawer schließen, Navigation bleibt
    const link = target.closest('#PsCartDrawer a[href]');
    if (link) close();
  });

  document.addEventListener('change', (event) => {
    const input = event.target instanceof Element ? event.target.closest('[data-ps-qty-input]') : null;
    if (input) scheduleChange(input);
  });

  document.addEventListener('keydown', (event) => {
    // Enter im Mengenfeld: Menge übernehmen statt zur Kasse zu senden
    const input = event.target instanceof Element ? event.target.closest('[data-ps-qty-input]') : null;
    if (input && event.key === 'Enter') {
      event.preventDefault();
      scheduleChange(input);
    }
  });

  document.addEventListener('input', (event) => {
    const input = event.target instanceof Element ? event.target.closest('[data-ps-cart-discount-input]') : null;
    if (!input) return;
    const view = input.closest('[data-ps-cart-view]') || document;
    const error = view.querySelector('[data-ps-cart-discount-error]');
    const field = view.querySelector('[data-ps-cart-discount-field]');
    if (error) error.textContent = '';
    if (field) field.classList.remove('ps-field--error');
    input.removeAttribute('aria-invalid');
    syncDiscountButtons(view);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form) return;

    if (form.matches('[data-ps-cart-discount-form]')) {
      event.preventDefault();
      const input = document.querySelector(`[data-ps-cart-discount-input][form="${CSS.escape(form.id)}"]`);
      applyDiscount(input ? input.value : '');
      return;
    }

    // Produktformulare mit data-ps-cart-form: AJAX statt Seitenwechsel (#40 Auto-open)
    if (form.matches('[data-ps-cart-form]')) {
      const data = new FormData(form);
      const id = data.get('id');
      if (!id) return;
      event.preventDefault();
      const submitter = event.submitter || form.querySelector('[type="submit"]');
      add(id, data.get('quantity') || 1, { trigger: submitter });
    }
  });

  // Rückkehr über den Browser-Verlauf (bfcache): Zähler und Drawer neu laden
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });

  views().forEach(syncDiscountButtons);

  PS.cart = { add, change, applyDiscount, open, close, refresh };
  PS.emit('cart:ready');
})();
